import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Activity, HeartPulse, Droplet, Thermometer, Wind, Sparkles,
  BellRing, ClipboardPlus, ArrowRightCircle, FileText, History, Cpu, Send,
  PlusCircle, RefreshCw, Brain, Loader2, Video,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid,
} from "recharts";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { api, formatApiError } from "@/lib/api";
import { VITAL_META, STATUS_COLORS, RISK_COLORS, INTERVENTION_TYPES, timeAgo } from "@/pages/rpm/constants";

const ICONS = {
  bp_systolic: Activity, heart_rate: HeartPulse,
  spo2: Droplet, temperature: Thermometer, respiratory_rate: Wind,
};

const TREND_CHARTS = ["bp_systolic", "heart_rate", "spo2", "temperature"];
const RANGE_OPTIONS = [
  { v: "24h", label: "24 Hours" }, { v: "48h", label: "48 Hours" },
  { v: "7d", label: "7 Days" }, { v: "30d", label: "30 Days" },
];

function VitalCard({ tile }) {
  const Icon = ICONS[tile.vital_type] || Activity;
  const meta = VITAL_META[tile.vital_type] || {};
  const c = STATUS_COLORS[tile.status] || STATUS_COLORS.missing;
  return (
    <div className="rounded-lg border-2 p-3" style={{ background: c.bg, borderColor: c.border + "55" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: c.text }} />
          <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: c.text }}>{meta.label}</span>
        </div>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold" style={{ color: c.text }}>{tile.value ?? "—"}</span>
        <span className="text-xs" style={{ color: c.text + "BB" }}>{tile.unit || meta.unit}</span>
      </div>
      <div className="text-[11px] mt-0.5" style={{ color: c.text + "AA" }}>{timeAgo(tile.taken_at)}</div>
    </div>
  );
}

function News2Breakdown({ n2 }) {
  if (!n2 || !n2.breakdown) return null;
  return (
    <div className="space-y-1.5">
      {Object.entries(n2.breakdown).map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-xs">
          <span className="text-[#6B7280] capitalize">{k.replace("_", " ")}</span>
          <span className={`font-semibold ${v >= 3 ? "text-[#EF4444]" : v >= 2 ? "text-[#F97316]" : v >= 1 ? "text-[#F59E0B]" : "text-[#10B981]"}`}>+{v}</span>
        </div>
      ))}
    </div>
  );
}

export default function RpmPatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("24h");
  const [history, setHistory] = useState({ by_type: {}, total: 0 });
  const [alerts, setAlerts] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [tab, setTab] = useState("overview");

  // AI narrative
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNarrative, setAiNarrative] = useState(null);

  // Submit reading
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    bp_systolic: "", bp_diastolic: "", heart_rate: "", spo2: "", temperature: "", respiratory_rate: "",
  });

  // Intervention dialog
  const [intervOpen, setIntervOpen] = useState(false);
  const [intervForm, setIntervForm] = useState({ alert_id: "", intervention_type: "", notes: "", outcome: "" });

  // Alert action dialog
  const [alertAction, setAlertAction] = useState(null); // { id, action, reason, notes }

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [d, h, a, iv, dv] = await Promise.all([
        api.get(`/rpm/patients/${id}`),
        api.get(`/rpm/patients/${id}/vitals`, { params: { range } }),
        api.get(`/rpm/alerts`, { params: { patient_id: id, page_size: 100 } }),
        api.get(`/rpm/interventions`, { params: { patient_id: id, page_size: 100 } }),
        api.get(`/rpm/devices`, { params: { patient_id: id } }),
      ]);
      setData(d.data); setHistory(h.data); setAlerts(a.data.items || []);
      setInterventions(iv.data.items || []); setDevices(dv.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id, range]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const trendData = useMemo(() => {
    // Format per chart: array of {t, value}
    const out = {};
    for (const vt of TREND_CHARTS) {
      out[vt] = (history.by_type[vt] || []).map((r) => ({
        t: new Date(r.taken_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
        value: r.value, status: r.status,
      }));
    }
    return out;
  }, [history]);

  async function submitReadings() {
    const items = [];
    for (const [vt, val] of Object.entries(submitForm)) {
      if (val !== "" && !isNaN(parseFloat(val))) items.push({ vital_type: vt, value: parseFloat(val) });
    }
    if (items.length === 0) { toast.error("Enter at least one value"); return; }
    try {
      const r = await api.post(`/rpm/patients/${id}/readings`, { readings: items });
      toast.success(`Submitted ${r.data.inserted.length} reading(s)${r.data.alerts_created?.length ? `, ${r.data.alerts_created.length} alert(s) triggered` : ""}`);
      setSubmitOpen(false);
      setSubmitForm({ bp_systolic: "", bp_diastolic: "", heart_rate: "", spo2: "", temperature: "", respiratory_rate: "" });
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function fetchAiNarrative() {
    setAiBusy(true);
    try {
      const r = await api.post(`/rpm/patients/${id}/risk/explain`);
      setAiNarrative(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(false); }
  }

  async function doAlertAction() {
    if (!alertAction) return;
    const { id: alertId, action, reason, notes } = alertAction;
    try {
      await api.post(`/rpm/alerts/${alertId}/${action}`, { reason, notes });
      toast.success(`Alert ${action}`);
      setAlertAction(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function openIntervention(alert) {
    setIntervForm({ alert_id: alert?.id || "", intervention_type: "", notes: "", outcome: "" });
    setIntervOpen(true);
  }
  async function saveIntervention() {
    if (!intervForm.intervention_type) { toast.error("Pick a type"); return; }
    try {
      await api.post("/rpm/interventions", {
        patient_id: id,
        alert_id: intervForm.alert_id || null,
        intervention_type: intervForm.intervention_type,
        notes: intervForm.notes, outcome: intervForm.outcome,
      });
      toast.success("Intervention recorded");
      setIntervOpen(false);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (loading || !data) {
    return <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>;
  }

  const p = data.patient;
  const enr = data.enrollment;
  const ai = data.ai_risk || {};
  const n2 = data.news2 || {};

  return (
    <div data-testid="rpm-patient-detail">
      <PageHeader
        title={`${p.first_name} ${p.last_name}`}
        subtitle={`${p.mrn} · ${p.age}y · ${p.gender || ""} · ${enr.primary_diagnosis || "RPM enrollment"}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/rpm")} className="border-[#E5E7EB]" data-testid="rpm-detail-back">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Command Center
            </Button>
            <Button variant="outline" onClick={async () => {
              try {
                const r = await api.post("/teleconsult/consultations", {
                  patient_id: id, consultation_type: "rpm_escalation",
                  priority: "urgent", duration_minutes: 30,
                  source_module: "rpm", source_id: id,
                  notes: "Initiated from RPM patient detail.",
                });
                toast.success("Teleconsultation created");
                navigate(`/teleconsult/room/${r.data.id}`);
              } catch (e) { toast.error(formatApiError(e)); }
            }} className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" data-testid="rpm-start-teleconsult">
              <Video className="w-4 h-4 mr-1.5" /> Start Teleconsult
            </Button>
            <Button onClick={() => setSubmitOpen(true)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="rpm-submit-reading">
              <Send className="w-4 h-4 mr-1.5" /> Submit Reading
            </Button>
          </div>
        }
      />

      {/* Overall risk strip */}
      <div className="hah-card p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <div className="hah-section-title">Overall Risk</div>
          <div className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{ background: (RISK_COLORS[data.overall_status] || RISK_COLORS.stable).bg, color: (RISK_COLORS[data.overall_status] || RISK_COLORS.stable).text }}>
            <span className="w-2 h-2 rounded-full" style={{ background: (RISK_COLORS[data.overall_status] || RISK_COLORS.stable).dot }} />
            <span className="font-semibold capitalize">{data.overall_status}</span>
          </div>
        </div>
        <div>
          <div className="hah-section-title">NEWS2</div>
          <div className="text-2xl font-bold text-[#1F2937] mt-1">{n2.score}</div>
          <div className="text-xs text-[#6B7280] capitalize">{n2.risk_level} risk</div>
        </div>
        <div>
          <div className="hah-section-title">AI Risk</div>
          <div className="text-2xl font-bold text-[#1F2937] mt-1 capitalize">{ai.risk_level}</div>
          <div className="text-xs text-[#6B7280]">{(ai.risk_score || 0).toFixed(0)}/100</div>
        </div>
        <div>
          <div className="hah-section-title">Recommended Action</div>
          <div className="text-sm font-semibold text-[#0B7E8C] mt-1">{data.recommended_action}</div>
          <div className="text-xs text-[#6B7280] mt-0.5">
            {data.active_alerts} active alert{data.active_alerts !== 1 ? "s" : ""} · {data.missing_count} missing
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1 bg-transparent h-auto p-0 mb-3">
          {[
            ["overview", "Overview"], ["trends", "Vital Trends"], ["news2", "NEWS2"],
            ["alerts", "Alerts"], ["interventions", "Interventions"],
            ["history", "Reading History"], ["device", "Device Status"], ["timeline", "Timeline"],
          ].map(([k, v]) => (
            <TabsTrigger key={k} value={k} className="hah-tab" data-testid={`rpm-tab-${k}`}>{v}</TabsTrigger>
          ))}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {(data.vital_tiles || []).map((t) => <VitalCard key={t.vital_type} tile={t} />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="hah-card p-5">
              <div className="hah-section-title">AI Deterioration Risk</div>
              <div className="flex items-center justify-between mt-2 mb-3">
                <div>
                  <div className="text-3xl font-bold text-[#1F2937] capitalize">{ai.risk_level}</div>
                  <div className="text-xs text-[#6B7280]">{(ai.risk_score || 0).toFixed(0)}/100 risk score</div>
                </div>
                <Button size="sm" onClick={fetchAiNarrative} disabled={aiBusy}
                  className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="rpm-ai-why">
                  {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Brain className="w-3.5 h-3.5 mr-1.5" />}
                  Explain — Why?
                </Button>
              </div>
              {ai.factors?.length > 0 && (
                <div className="space-y-1 mb-3">
                  {ai.factors.map((f, i) => (
                    <div key={i} className="text-xs text-[#6B7280] flex items-start gap-2">
                      <Sparkles className="w-3 h-3 mt-0.5 text-[#8B5CF6]" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              )}
              {aiNarrative && (
                <div className="mt-2 p-3 rounded-lg bg-[#F5F3FF] border border-[#8B5CF6]/30 text-sm text-[#1F2937]" data-testid="rpm-ai-narrative">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8B5CF6] mb-1">
                    {aiNarrative.model === "fallback" ? "Deterministic explanation" : `Powered by ${aiNarrative.model}`}
                  </div>
                  {aiNarrative.narrative}
                </div>
              )}
            </div>
            <div className="hah-card p-5">
              <div className="hah-section-title">NEWS2 Breakdown</div>
              <div className="text-3xl font-bold text-[#1F2937] mt-2">{n2.score}</div>
              <div className="text-xs text-[#6B7280] capitalize mb-3">{n2.risk_level} risk</div>
              <News2Breakdown n2={n2} />
              <div className="mt-3 p-2 rounded bg-[#EAF8FA] border border-[#0B7E8C]/20 text-xs text-[#1F2937]">
                <strong className="text-[#0B7E8C]">Recommended: </strong>{n2.recommended_action}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* TRENDS */}
        <TabsContent value="trends" className="mt-3">
          <div className="flex items-center justify-end mb-3 gap-2">
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-40" data-testid="rpm-trends-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadAll} className="border-[#E5E7EB]" data-testid="rpm-trends-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TREND_CHARTS.map((vt) => {
              const meta = VITAL_META[vt];
              const items = trendData[vt] || [];
              return (
                <div key={vt} className="hah-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-[#1F2937]">{meta.label}</h3>
                    <span className="text-xs text-[#6B7280]">{items.length} readings</span>
                  </div>
                  {items.length === 0 ? (
                    <div className="text-xs text-[#94A3B8] italic py-6 text-center">No data in selected range.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={items} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="t" tick={{ fontSize: 10, fill: "#6B7280" }} hide={items.length > 12} />
                        <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} width={28} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Line type="monotone" dataKey="value" stroke="#0B7E8C" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* NEWS2 */}
        <TabsContent value="news2" className="mt-3">
          <div className="hah-card p-5">
            <div className="hah-section-title">Current NEWS2</div>
            <div className="text-5xl font-bold text-[#1F2937] mt-2">{n2.score}</div>
            <div className="text-sm text-[#6B7280] capitalize mb-4">{n2.risk_level} risk</div>
            <News2Breakdown n2={n2} />
            <div className="mt-4 p-3 rounded bg-[#EAF8FA] border border-[#0B7E8C]/20 text-sm text-[#1F2937]">
              <strong className="text-[#0B7E8C]">Recommended action: </strong>{n2.recommended_action}
            </div>
          </div>
        </TabsContent>

        {/* ALERTS */}
        <TabsContent value="alerts" className="mt-3">
          {alerts.length === 0 ? (
            <div className="hah-card p-8 text-center text-sm text-[#6B7280]">No alerts.</div>
          ) : (
            <div className="space-y-2">
              {alerts.map((a) => (
                <div key={a.id} className="hah-card p-4" data-testid={`rpm-alert-${a.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${
                          a.severity === "critical" ? "bg-[#FEE2E2] text-[#991B1B]" :
                          a.severity === "warning" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#E0F2FE] text-[#075985]"
                        }`}>{a.severity}</span>
                        <span className="text-[10px] uppercase font-semibold text-[#6B7280]">{a.source}</span>
                        <span className="text-[10px] uppercase font-semibold text-[#0B7E8C]">{a.status}</span>
                      </div>
                      <div className="text-sm font-semibold text-[#1F2937]">{a.title}</div>
                      <div className="text-xs text-[#6B7280] mt-0.5">{timeAgo(a.created_at)} · {a.description}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {a.status === "new" && (
                        <Button size="sm" variant="outline" className="h-7 border-[#E5E7EB] text-xs"
                          onClick={() => setAlertAction({ id: a.id, action: "acknowledge", notes: "" })}
                          data-testid={`rpm-alert-ack-${a.id}`}>Ack</Button>
                      )}
                      {["new", "acknowledged", "in_progress"].includes(a.status) && (
                        <Button size="sm" variant="outline" className="h-7 border-[#E5E7EB] text-xs"
                          onClick={() => setAlertAction({ id: a.id, action: "escalate", reason: "" })}
                          data-testid={`rpm-alert-esc-${a.id}`}>Escalate</Button>
                      )}
                      {["new", "acknowledged", "in_progress", "escalated"].includes(a.status) && (
                        <Button size="sm" variant="outline" className="h-7 border-[#E5E7EB] text-xs"
                          onClick={() => openIntervention(a)}
                          data-testid={`rpm-alert-interv-${a.id}`}>
                          <ClipboardPlus className="w-3 h-3 mr-1" /> Intervention
                        </Button>
                      )}
                      {["new", "acknowledged", "in_progress", "escalated"].includes(a.status) && (
                        <Button size="sm" variant="outline" className="h-7 border-[#E5E7EB] text-xs"
                          onClick={() => setAlertAction({ id: a.id, action: "resolve", notes: "" })}
                          data-testid={`rpm-alert-res-${a.id}`}>Resolve</Button>
                      )}
                      {a.status !== "dismissed" && a.status !== "closed" && (
                        <Button size="sm" variant="outline" className="h-7 border-[#EF444433] text-[#EF4444] text-xs"
                          onClick={() => setAlertAction({ id: a.id, action: "dismiss", reason: "" })}
                          data-testid={`rpm-alert-dis-${a.id}`}>Dismiss</Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* INTERVENTIONS */}
        <TabsContent value="interventions" className="mt-3">
          <div className="flex items-center justify-end mb-3">
            <Button onClick={() => openIntervention(null)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="rpm-add-interv">
              <PlusCircle className="w-4 h-4 mr-1.5" /> Add intervention
            </Button>
          </div>
          {interventions.length === 0 ? (
            <div className="hah-card p-8 text-center text-sm text-[#6B7280]">No interventions yet.</div>
          ) : (
            <div className="space-y-2">
              {interventions.map((iv) => (
                <div key={iv.id} className="hah-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-[#1F2937]">{INTERVENTION_TYPES[iv.intervention_type] || iv.intervention_type}</div>
                      {iv.notes && <div className="text-xs text-[#6B7280] mt-0.5">{iv.notes}</div>}
                      {iv.outcome && <div className="text-xs text-[#0B7E8C] mt-1">Outcome: {iv.outcome}</div>}
                    </div>
                    <div className="text-xs text-[#94A3B8]">{timeAgo(iv.performed_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* READING HISTORY */}
        <TabsContent value="history" className="mt-3">
          <div className="hah-card p-2">
            <div className="overflow-auto">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
                  <tr><th className="text-left p-2.5">Vital</th><th className="text-left p-2.5">Value</th><th className="text-left p-2.5">Status</th><th className="text-left p-2.5">Source</th><th className="text-left p-2.5">Taken at</th></tr>
                </thead>
                <tbody>
                  {Object.values(history.by_type).flat().sort((a, b) => (b.taken_at || "").localeCompare(a.taken_at || "")).slice(0, 200).map((r) => (
                    <tr key={r.id} className="border-t border-[#E5E7EB]">
                      <td className="p-2.5 capitalize">{(VITAL_META[r.vital_type] || {}).label || r.vital_type}</td>
                      <td className="p-2.5 font-mono">{r.value} {r.unit}</td>
                      <td className="p-2.5">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                          style={{ background: (STATUS_COLORS[r.status] || STATUS_COLORS.normal).chip,
                                   color: (STATUS_COLORS[r.status] || STATUS_COLORS.normal).text }}>
                          {r.status || "normal"}
                        </span>
                      </td>
                      <td className="p-2.5 text-[#6B7280]">{r.source}</td>
                      <td className="p-2.5 text-[#6B7280]">{r.taken_at && new Date(r.taken_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          </div>
        </TabsContent>

        {/* DEVICE */}
        <TabsContent value="device" className="mt-3">
          {devices.length === 0 ? (
            <div className="hah-card p-8 text-center text-sm text-[#6B7280]">No devices registered.</div>
          ) : (
            <div className="space-y-2">
              {devices.map((d) => (
                <div key={d.id} className="hah-card p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-[#0B7E8C]" />
                      <span className="text-sm font-semibold text-[#1F2937]">{d.device_type}</span>
                      <span className="text-xs font-mono text-[#6B7280]">{d.serial_number}</span>
                    </div>
                    <div className="text-xs text-[#6B7280] mt-0.5">Last seen {timeAgo(d.last_seen_at)} · Battery {d.battery_pct ?? "—"}%</div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
                    style={{ background: d.computed_status === "active" ? "#D1FAE5" : "#FEE2E2",
                             color:      d.computed_status === "active" ? "#065F46" : "#991B1B" }}>
                    {d.computed_status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TIMELINE — alerts + interventions interleaved */}
        <TabsContent value="timeline" className="mt-3">
          {(() => {
            const items = [
              ...alerts.map((a) => ({ when: a.created_at, kind: "alert", text: a.title, sub: a.description, color: "#EF4444" })),
              ...interventions.map((iv) => ({ when: iv.performed_at, kind: "intervention", text: INTERVENTION_TYPES[iv.intervention_type] || iv.intervention_type, sub: iv.notes, color: "#0B7E8C" })),
            ].sort((a, b) => (b.when || "").localeCompare(a.when || ""));
            if (items.length === 0) return <div className="hah-card p-8 text-center text-sm text-[#6B7280]">No timeline events yet.</div>;
            return (
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="flex items-start gap-3 hah-card p-3">
                    <span className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: it.color + "22", color: it.color }}>
                      {it.kind === "alert" ? <BellRing className="w-3.5 h-3.5" /> : <ClipboardPlus className="w-3.5 h-3.5" />}
                    </span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-[#1F2937]">{it.text}</div>
                      {it.sub && <div className="text-xs text-[#6B7280] mt-0.5">{it.sub}</div>}
                    </div>
                    <div className="text-xs text-[#94A3B8] shrink-0">{timeAgo(it.when)}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* Submit reading dialog */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Submit Vital Reading</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            {["bp_systolic", "bp_diastolic", "heart_rate", "spo2", "temperature", "respiratory_rate"].map((vt) => (
              <FormField key={vt} label={`${VITAL_META[vt].label} (${VITAL_META[vt].unit})`}>
                <Input type="number" step="0.1" value={submitForm[vt]}
                  onChange={(e) => setSubmitForm({ ...submitForm, [vt]: e.target.value })}
                  data-testid={`rpm-submit-${vt}`} />
              </FormField>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={submitReadings} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="rpm-submit-confirm">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Intervention dialog */}
      <Dialog open={intervOpen} onOpenChange={setIntervOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Intervention</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <FormField label="Type" required>
              <Select value={intervForm.intervention_type} onValueChange={(v) => setIntervForm({ ...intervForm, intervention_type: v })}>
                <SelectTrigger data-testid="rpm-detail-interv-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(INTERVENTION_TYPES).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Notes" full>
              <Textarea rows={3} value={intervForm.notes} onChange={(e) => setIntervForm({ ...intervForm, notes: e.target.value })} data-testid="rpm-detail-interv-notes" />
            </FormField>
            <FormField label="Outcome" full>
              <Input value={intervForm.outcome} onChange={(e) => setIntervForm({ ...intervForm, outcome: e.target.value })} data-testid="rpm-detail-interv-outcome" />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIntervOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={saveIntervention} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="rpm-detail-interv-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert action dialog */}
      <Dialog open={!!alertAction} onOpenChange={(o) => { if (!o) setAlertAction(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="capitalize">{alertAction?.action} Alert</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {(alertAction?.action === "escalate" || alertAction?.action === "dismiss") && (
              <FormField label="Reason" required full>
                <Textarea rows={3} value={alertAction?.reason || ""}
                  onChange={(e) => setAlertAction({ ...alertAction, reason: e.target.value })}
                  data-testid="rpm-alert-action-reason" />
              </FormField>
            )}
            {(alertAction?.action === "acknowledge" || alertAction?.action === "resolve") && (
              <FormField label={alertAction?.action === "resolve" ? "Intervention notes (required)" : "Notes"} full required={alertAction?.action === "resolve"}>
                <Textarea rows={3} value={alertAction?.notes || ""}
                  onChange={(e) => setAlertAction({ ...alertAction, notes: e.target.value })}
                  data-testid="rpm-alert-action-notes" />
              </FormField>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertAction(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={doAlertAction} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="rpm-alert-action-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
